import { useEffect, useRef } from 'react';
import { GroupedSummaryDto, ResourceSummaryDto } from './types';

type SummaryDomain = 'adoption' | 'security' | 'productivity' | 'utilisation';

type SummaryNoDataDiagnosticsOptions = {
  summaryEnabled: boolean;
  isSummaryLoading: boolean;
  groupedSummary: GroupedSummaryDto | undefined;
  mappedData: unknown;
  legacyParams: unknown;
  domain: SummaryDomain;
  diagnosticTag?: string;
};

type ResourceDiagnostic = {
  totalCount: number;
  lastUpdatedAt: string;
  countsByFilterKeyCount: number;
  aggregateKeyCount: number;
  timeSeriesKeyCount: number;
  nonEmptyTimeSeriesKeyCount: number;
  sampleCountKeys: string[];
  sampleAggregateKeys: string[];
  sampleTimeSeriesKeys: string[];
};

const SAMPLE_KEY_LIMIT = 12;

function toObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, unknown>;
}

function safeSerialize(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable-value]';
  }
}

function describeMappedData(value: unknown): Record<string, unknown> {
  if (value === null) return { kind: 'null' };
  if (value === undefined) return { kind: 'undefined' };

  if (Array.isArray(value)) {
    return {
      kind: 'array',
      length: value.length,
      preview: value.slice(0, 3),
    };
  }

  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    const keys = Object.keys(objectValue);

    return {
      kind: 'object',
      keyCount: keys.length,
      keys: keys.slice(0, SAMPLE_KEY_LIMIT),
    };
  }

  if (typeof value === 'string') {
    return {
      kind: 'string',
      length: value.length,
      preview: value.slice(0, 64),
    };
  }

  return {
    kind: typeof value,
    value,
  };
}

function hasMappedSummaryData(value: unknown): boolean {
  if (value === null || value === undefined) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return false;
    return entries.some(([, entryValue]) => hasMappedSummaryData(entryValue));
  }

  return false;
}

function summarizeResource(resource: ResourceSummaryDto): ResourceDiagnostic {
  const countKeys = Object.keys(resource.countsByFilter ?? {});
  const aggregateKeys = Object.keys(resource.aggregates ?? {});
  const timeSeriesEntries = Object.entries(resource.timeSeries ?? {});

  return {
    totalCount: resource.totalCount ?? 0,
    lastUpdatedAt: resource.lastUpdatedAt ?? '',
    countsByFilterKeyCount: countKeys.length,
    aggregateKeyCount: aggregateKeys.length,
    timeSeriesKeyCount: timeSeriesEntries.length,
    nonEmptyTimeSeriesKeyCount: timeSeriesEntries.filter(([, points]) => Array.isArray(points) && points.length > 0).length,
    sampleCountKeys: countKeys.slice(0, SAMPLE_KEY_LIMIT),
    sampleAggregateKeys: aggregateKeys.slice(0, SAMPLE_KEY_LIMIT),
    sampleTimeSeriesKeys: timeSeriesEntries
      .filter(([, points]) => Array.isArray(points) && points.length > 0)
      .map(([key]) => key)
      .slice(0, SAMPLE_KEY_LIMIT),
  };
}

function summarizeGroupedSummary(groupedSummary: GroupedSummaryDto | undefined): {
  hasGroupedSummary: boolean;
  resourceCount: number;
  availableResources: string[];
  resourceDiagnostics: Record<string, ResourceDiagnostic>;
  fingerprint: string;
} {
  const summaries = toObject(groupedSummary?.summaries);
  const availableResources = Object.keys(summaries).sort();

  const resourceDiagnostics = Object.fromEntries(
    availableResources.map((resourceType) => {
      const resource = summaries[resourceType] as ResourceSummaryDto;
      return [resourceType, summarizeResource(resource)];
    })
  ) as Record<string, ResourceDiagnostic>;

  return {
    hasGroupedSummary: !!groupedSummary,
    resourceCount: availableResources.length,
    availableResources,
    resourceDiagnostics,
    fingerprint: safeSerialize(
      availableResources.map((resourceType) => {
        const resource = resourceDiagnostics[resourceType];
        return {
          resourceType,
          totalCount: resource.totalCount,
          lastUpdatedAt: resource.lastUpdatedAt,
          countsByFilterKeyCount: resource.countsByFilterKeyCount,
          aggregateKeyCount: resource.aggregateKeyCount,
          timeSeriesKeyCount: resource.timeSeriesKeyCount,
        };
      })
    ),
  };
}

export function useSummaryNoDataDiagnostics(options: SummaryNoDataDiagnosticsOptions): void {
  const lastLoggedFingerprintRef = useRef<string>('');

  useEffect(() => {
    if (!options.summaryEnabled || options.isSummaryLoading) {
      return;
    }

    if (hasMappedSummaryData(options.mappedData)) {
      return;
    }

    const summaryDiagnostics = summarizeGroupedSummary(options.groupedSummary);
    const fingerprint = safeSerialize({
      domain: options.domain,
      diagnosticTag: options.diagnosticTag ?? '',
      params: options.legacyParams,
      summaryFingerprint: summaryDiagnostics.fingerprint,
      mappedData: describeMappedData(options.mappedData),
    });

    if (fingerprint === lastLoggedFingerprintRef.current) {
      return;
    }

    lastLoggedFingerprintRef.current = fingerprint;

    console.error('[Summary API] No mapped data returned from summary response.', {
      domain: options.domain,
      diagnosticTag: options.diagnosticTag ?? 'not-specified',
      occurredAtUtc: new Date().toISOString(),
      legacyParams: options.legacyParams,
      mappedDataDescriptor: describeMappedData(options.mappedData),
      summaryDiagnostics,
      pipelineCorrectionHints: [
        'Verify the summary endpoint emitted the resource expected by this tile.',
        'Verify pipeline transforms produced required precomputedShapes/aggregates for the selected params.',
        'Verify lastUpdatedAt corresponds to the expected pipeline run and tenant/date partition.',
        'Do not add frontend fallback data; correct the missing summary resource/shape in pipeline output.',
      ],
      stackTrace: new Error('SummaryNoDataDiagnostic').stack,
    });
  }, [
    options.summaryEnabled,
    options.isSummaryLoading,
    options.groupedSummary,
    options.mappedData,
    options.legacyParams,
    options.domain,
    options.diagnosticTag,
  ]);
}
